import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CustomSpinnerComponent } from '@core/components/ui-custom-spinner/custom-spinner.component';
import type { DetailsModel } from '@core/components/ui-clinng-status-alert/ui-clinng-status-alert.component';
import type { TicketDetails } from '@core/components/ui-clinng-ticket-info/ui-clinng-ticket-info.component';
import { UiClinngButtonComponent } from '@core/components/ui-clinng-button/ui-clinng-button.component';
import { UiClinngDownloadButtonComponent } from '@core/components/ui-clinng-download-button/ui-clinng-download-button.component';
import { UiClinngStaticHeaderComponent } from '@core/components/ui-clinng-static-header/ui-clinng-static-header.component';
import { UiClinngStatusAlertComponent } from '@core/components/ui-clinng-status-alert/ui-clinng-status-alert.component';
import { UiClinngTicketInfoComponent } from '@core/components/ui-clinng-ticket-info/ui-clinng-ticket-info.component';
import { DownloadPseVoucherComponent } from '@features/checkout-form-pse/components/download-pse-voucher/download-pse-voucher.component';
import { CaptureScreenService } from '@core/services/capture-screen.service';
import { MoviiService } from '@core/services/movii.service';
import type { GetOperationMoviiResponse } from '@core/services/movii.interface';
import { IconsEnum } from '@core/models/icons.enum';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { finalize, timeout } from 'rxjs';
import { deferViewUpdate } from '@core/utils/defer-view-update';

@Component({
  selector: 'checkout-status-pse',
  standalone: true,
  imports: [
    CommonModule,
    CustomSpinnerComponent,
    UiClinngButtonComponent,
    UiClinngDownloadButtonComponent,
    UiClinngTicketInfoComponent,
    UiClinngStatusAlertComponent,
    UiClinngStaticHeaderComponent,
    DownloadPseVoucherComponent,
  ],
  templateUrl: './checkout-status-pse.component.html',
  styleUrl: './checkout-status-pse.component.scss',
})
export class CheckoutStatusPseComponent implements OnInit {
  screenRecordService = inject(CaptureScreenService);
  detailsModel!: DetailsModel;
  operationInfo!: GetOperationMoviiResponse;
  ticketDetails: TicketDetails[] = [];
  download: boolean = false;
  transactionId!: string;
  verification!: string;
  initLoading = true;
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private moviiService: MoviiService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.transactionId = this.route.snapshot.paramMap.get('transactionId') ?? '';
    this.verification = this.route.snapshot.queryParamMap.get('verification') ?? '';

    if (!this.transactionId || !this.verification) {
      deferViewUpdate(this.cdr, () => {
        this.initLoading = false;
        this.router.navigate(['/error', 'movii']);
      });
      return;
    }

    this.moviiService.getOperation(this.transactionId, this.verification)
      .pipe(
        timeout(30_000),
        finalize(() => {
          deferViewUpdate(this.cdr, () => {
            this.initLoading = false;
          });
        }),
      )
      .subscribe({
        next: (data: GetOperationMoviiResponse) => {
          deferViewUpdate(this.cdr, () => {
            this.operationInfo = data;
            this.setTicketDetails();
            this.setAlertDetails();
          });
        },
        error: (err: unknown) => {
          console.error(err);
          deferViewUpdate(this.cdr, () => {
            this.router.navigate(['/error', 'movii']);
          });
        },
      });
  }
  downloadVoucher() {
    this.download = true;
    setTimeout(() => {
      this.screenRecordService.captureWithPromise('comprobante-pago').then((imageData: string) => {
        this.download = false;
        const link = document.createElement('a');
        link.href = imageData;
        link.download = 'pse.png';
        link.click();
      });
    }, 100);
  }

  redirect() {
    if (this.operationInfo.operation.state === '5') {
      window.location.href = this.operationInfo.operation.okUrl;
    } else {
      window.location.href = this.operationInfo.operation.errorUrl;
    }
  }

  setTicketDetails() {
    this.ticketDetails = [
      {
        title: 'Nombres y apellidos',
        value: `${this.operationInfo?.customer?.name}`
      },
      {
        title: 'Monto',
        value: `${this.operationInfo?.operation?.currencySymbol} ${this.operationInfo?.operation?.amount}`
      },
      {
        title: 'Banco',
        value: `${this.operationInfo?.bank?.name}`
      },
      {
        title: 'Número de solicitud',
        value: this.operationInfo?.operation?.idOperation
      },
      {
        title: 'Fecha y hora',
        value: moment(this.operationInfo?.operation?.creationDate).format('DD/MM/YYYY HH:mm')
      }
    ]
  }

  setAlertDetails() {
    if (this.operationInfo?.operation?.state === '5') {
      this.detailsModel = {
        title: 'Pago exitoso',
        mount: `${this.operationInfo?.operation?.currencySymbol} ${this.operationInfo?.operation?.amount}`,
        img: IconsEnum.SUCCESS,
        color: 'blue',
      }
    } else if (this.operationInfo?.operation?.state === '3' ) {
      this.router.navigate(['/error-status', 'movii']);
    } else if (this.operationInfo?.operation?.state === '6') {
      this.detailsModel = {
        title: 'Pago rechazado',
        mount: `${this.operationInfo?.operation?.currencySymbol} ${this.operationInfo?.operation?.amount}`,
        img: IconsEnum.ERROR,
        color: 'red',
      }
    } else {
      this.detailsModel = {
        title: 'Pago en proceso',
        subtitle: 'PSE esta procesando tu pago, espera unos minutos',
        mount: `${this.operationInfo?.operation?.currencySymbol} ${this.operationInfo?.operation?.amount}`,
        img: IconsEnum.IN_PROGRESS,
        color: 'blue',
      }
    }
  }

  showDownloadButton() {
    return  this.operationInfo?.operation?.state === '6' || this.operationInfo?.operation?.state === '5';
  }
}
